import {Component, forwardRef, Input, OnDestroy} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormArray,
    FormBuilder,
    FormGroup,
    NG_VALUE_ACCESSOR
} from '@angular/forms';
import {faCheck, faPlus, faTrash} from '@fortawesome/pro-solid-svg-icons';
import {Subscription} from 'rxjs';
import {ConstHTMLReportExpr, EnumReportExpr, ReportExpr} from '../../../../../report/report_types';

@Component({
    selector: 'wm-report-parameter-editor',
    templateUrl: './report-parameter-editor.component.html',
    styleUrls: ['./report-parameter-editor.component.less'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ReportParameterEditorComponent),
            multi: true
        }
    ]
})
export class ReportParameterEditorComponent implements ControlValueAccessor, OnDestroy {
    @Input() allowAdd: boolean = true;

    parametersArray: FormArray;

    private subscription?: Subscription;
    private onChange: (value: {[name: string]: ReportExpr}) => void = () => {};
    private onTouched: () => void = () => {};

    constructor(private fb: FormBuilder) {
        this.parametersArray = this.fb.array([]);
    }

    writeValue(map: {[name: string]: ReportExpr} | null): void {
        this.subscription?.unsubscribe();
        const entries = Object.entries(map || {});
        this.parametersArray = this.fb.array(
            entries.map(([key, expr]) => this.makeGroup(key, expr))
        );
        this.subscription = this.parametersArray.valueChanges.subscribe(() => {
            this.onChange(this.buildMap());
        });
        // Emit normalized value so the parent FormControl always reflects processed
        // ReportExpr data, even when the user never interacts with the form.
        setTimeout(() => this.onChange(this.buildMap()));
    }

    registerOnChange(fn: (value: {[name: string]: ReportExpr}) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    addParameter(): void {
        this.parametersArray.push(
            this.makeGroup('', {type: 'ConstHTMLStringReportExpr', value: ''})
        );
        this.onChange(this.buildMap());
    }

    removeParameter(index: number): void {
        this.parametersArray.removeAt(index);
        this.onChange(this.buildMap());
    }

    asFormGroup(control: AbstractControl): FormGroup {
        return control as FormGroup;
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }

    private makeGroup(key: string, rawExpr: any): FormGroup {
        const expr = this.toReportExpr(rawExpr);
        return this.fb.group({
            key,
            exprType: expr.type,
            value: this.valueFromExpr(expr),
            options: this.fb.control(expr.type === 'EnumReportExpr' ? (expr as EnumReportExpr).options : [])
        });
    }

    private toReportExpr(raw: any): ReportExpr {
        if (raw?.type === 'EnumReportExpr') return raw as EnumReportExpr;
        if (raw?.type === 'ImageReportExpr') return raw;
        // Fall back to a string expr, reading .value if present, otherwise the raw value itself
        const strValue = typeof raw?.value === 'string' ? raw.value : (typeof raw === 'string' ? raw : '');
        return {type: 'ConstHTMLStringReportExpr', value: strValue};
    }

    private valueFromExpr(expr: ReportExpr): string {
        switch (expr.type) {
            case 'ConstHTMLStringReportExpr': return (expr as ConstHTMLReportExpr).value;
            case 'EnumReportExpr': return (expr as EnumReportExpr).value;
            case 'ImageReportExpr': return '';
        }
    }

    private buildMap(): {[name: string]: ReportExpr} {
        const result: {[name: string]: ReportExpr} = {};
        for (const control of this.parametersArray.controls as FormGroup[]) {
            const v = control.value;
            if (!v.key) continue;
            result[v.key] = this.exprFromFormValue(v);
        }
        return result;
    }

    private exprFromFormValue(v: any): ReportExpr {
        switch (v.exprType) {
            case 'EnumReportExpr':
                return {type: 'EnumReportExpr', options: v.options, value: v.value};
            case 'ImageReportExpr':
                return {type: 'ImageReportExpr', imageId: v.value};
            default:
                return {type: 'ConstHTMLStringReportExpr', value: v.value};
        }
    }

    protected readonly faCheck = faCheck;
    protected readonly faPlus = faPlus;
    protected readonly faTrash = faTrash;
}
